import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import {
  useCreateWorldBoxCompanyMutation,
  useGetCurrentUserQuery,
} from "../../../../store/Company/companyApiSlice";
import { clearUserInfo } from "../../../../store/auth/authSlice";
import { selectActiveCompany } from "../../../../store/activeCompanySlice";
import { buildWebProfileSummary } from "../../../../constants/webProfilePricing";
import { getCountryCode, getStatesByCountryCode } from "../../../../utils/countriesState";
import { CommonToast } from "../../../common/Toast";
import {
  EMPTY_ORDER,
  EMPTY_REPRESENTATIVE,
  activeSubscription,
  orderPayload,
  placesOrderDirectly,
  reportModeFor,
  spendableEnterprises,
  validateOrder,
} from "./orderWebProfileFields";

/* Ordering a web profile. TRANSCRIBED from pages/public/CreateCompany.jsx: the same two mutations, the same payload, the same credit/enterprise branch and the... */
export const useOrderWebProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, userInfo } = useSelector((state) => state.auth);
  const activeCompany = useSelector(selectActiveCompany);

  const [createWorldBoxCompany, { isLoading: ordering }] =
    useCreateWorldBoxCompanyMutation();
  const { data: currentUser, refetch } = useGetCurrentUserQuery({ token });

  const [values, setValues] = useState(EMPTY_ORDER);
  const [rep, setRep] = useState(EMPTY_REPRESENTATIVE);
  const [acknowledged, setAcknowledged] = useState(false);
  const [errors, setErrors] = useState({});
  const [states, setStates] = useState([]);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [creditNoticeOpen, setCreditNoticeOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [selectedEnterprise, setSelectedEnterprise] = useState();

  const countries = useMemo(() => getCountryCode(), []);

  /* Transcribed: the price is the AED web-profile rate, at the member's
     discount when the active company is a member or verified. */
  const reportMode = reportModeFor(activeCompany);
  const summary = buildWebProfileSummary({ mode: reportMode });

  /* Transcribed: enterprises with credit to spend, from the signed-in user. */
  const enterprises = useMemo(() => spendableEnterprises(userInfo), [userInfo]);
  const subscription = activeSubscription(currentUser);
  const creditsLeft = subscription?.no_of_profile_views ?? 0;
  const creditBanner = creditsLeft >= 1;

  /* Transcribed: no token means the session is gone. */
  useEffect(() => {
    if (token == null) setSessionExpired(true);
  }, [token]);

  /* Transcribed: a stale part-finished order must not leak into this one. */
  useEffect(() => {
    localStorage.removeItem("payasyougoCompanyID");
    localStorage.removeItem("worldboxData");
  }, []);

  /* TIP-556, transcribed: arriving from the search "Company Not Found?" call
     to action carries the failed search, so the name and country are filled
     in for you. */
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (!prefill) return;

    setValues((prev) => ({
      ...prev,
      companyName: prefill.companyName || prev.companyName,
      ...(prefill.countryCode &&
      countries.some((c) => c.countryCode === prefill.countryCode)
        ? { country: prefill.countryCode }
        : {}),
    }));
    if (
      prefill.countryCode &&
      countries.some((c) => c.countryCode === prefill.countryCode)
    ) {
      getStatesByCountryCode(prefill.countryCode).then(setStates).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  /* Transcribed: choosing a country loads its states. The live handler leaves
     a stale state selected; cleared here, because a state from the previous
     country is worse than none. */
  const setCountry = async (code) => {
    setValues((prev) => ({ ...prev, country: code || "", state: "" }));
    setErrors((prev) => ({ ...prev, country: "" }));
    if (!code) {
      setStates([]);
      return;
    }
    try {
      setStates((await getStatesByCountryCode(code)) || []);
    } catch {
      /* A country with no state list is normal — the field falls back to free
         text, as the live one does. */
      setStates([]);
    }
  };

  const setRepField = (key, value) => setRep((prev) => ({ ...prev, [key]: value }));

  const closeSession = () => {
    setSessionExpired(false);
    dispatch(clearUserInfo());
    navigate("/login");
  };

  /* Transcribed: the plan handed to the payment screen. */
  const planDetails = {
    title: "Web Profile",
    mode: reportMode,
    slug: "pay-as-you-go",
  };

  const submit = async (type) => {
    const found = validateOrder(values);
    setErrors(found);
    if (Object.keys(found).length > 0) return false;

    const data = orderPayload({
      values,
      type,
      acknowledged,
      rep,
      enterpriseId: selectedEnterprise?.id,
    });

    if (placesOrderDirectly(type)) {
      try {
        const res = await createWorldBoxCompany({ data, token }).unwrap();
        if (res?.status === "ORDER_CREATED") {
          refetch();
          setSuccessOpen(true);
          setCreditNoticeOpen(false);
          CommonToast.success(
            type === "credit"
              ? `Your order has been placed successfully with Order No.:${res.ordernumber}`
              : "Your order has been placed successfully"
          );
          return true;
        }
        return false;
      } catch (error) {
        CommonToast.error(error?.data?.response ?? "Something went wrong");
        return false;
      }
    }

    /* Transcribed handoff: the company is created by the webhook after
       payment, so there is no companyID yet — `reportRequest` tells the
       payment screen to skip its select-a-company guard. */
    localStorage.setItem("companyDescription", JSON.stringify(data));
    localStorage.setItem("createWorldboxCompany", true);
    navigate("/plans/paymentdetails", {
      state: { planDetails, reportRequest: true },
    });
    return true;
  };

  /* Transcribed: paying by credit validates first, then asks. */
  const startCreditPurchase = () => {
    const found = validateOrder(values);
    setErrors(found);
    if (Object.keys(found).length > 0) return false;
    setCreditNoticeOpen(true);
    return true;
  };

  /* Transcribed: where the success modal sends you. */
  const finishSuccess = () => {
    setSuccessOpen(false);
    navigate(selectedEnterprise ? "/dashboard/enterprise" : "/dashboard/purchased");
  };

  return {
    values,
    setField,
    setCountry,
    countries,
    states,
    errors,
    rep,
    setRepField,
    acknowledged,
    setAcknowledged,
    summary,
    reportMode,
    ordering,
    submit,
    startCreditPurchase,
    creditBanner,
    creditsLeft,
    enterprises,
    subscription,
    selectedEnterprise,
    setSelectedEnterprise,
    creditNoticeOpen,
    closeCreditNotice: () => setCreditNoticeOpen(false),
    successOpen,
    finishSuccess,
    sessionExpired,
    closeSession,
    userInfo,
  };
};

export default useOrderWebProfile;
